/**
 * FetchCommunityMessagesUseCase
 *
 * Paginated fetch of a creator's community-wall messages. Produces
 * `CommunityChatMessageEntity[]` newest-first (desc by `createdTimestamp`) and
 * reports whether a subsequent page exists, along with the cursor
 * (`lastTimestamp`) the hook (Task 9/10) feeds back to advance pagination.
 *
 * Flutter source: lib/presentation/screens/community/community_screen.dart:250-278
 *   (`getMessages`) — fetch one page via
 *   `CommunityRepository.getMessages(owner, lastFetchedDocument, communityMessagesPagination)`,
 *   append to `_messages`, update `_lastFetchedDocument`, and set `_hasMore`.
 *   - `community_data_source.dart:74` — `hasMore = querySnapshot.docs.length == limit`
 *     (strict equality, NOT `>=`).
 *   - `community_data_source.dart:62` — the page is ordered by `createdTimestamp`
 *     descending; the post-fetch sort below re-asserts that defensively.
 *   - `app_constants.dart:11` — `messagesListPagination = 20`
 *     (`SERVER_FETCH_CONSTANTS.MESSAGES_LIST_PAGINATION`).
 *
 * Pure-domain contract (conventions.md § Clean Architecture flow):
 *   - Calls `CommunityService.fetchMessagesPage`; does NOT touch
 *     `firestoreService` directly and does NOT dispatch to Redux.
 *   - Community messages are NOT end-to-end encrypted — `community_screen.dart`
 *     has no decrypt step — so this use case carries no E2EE decryption fan-out
 *     (the one structural difference from `FetchChatMessagesUseCase`).
 */

import { CommunityService } from '@/domain/services/community/communityService';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';
import { sortCommunityMessagesByCreatedDesc } from '@/domain/utils/communityUtils';

export interface FetchCommunityMessagesResult {
  messages: CommunityChatMessageEntity[];
  /**
   * The `createdTimestamp` of the last (oldest, in this desc-ordered page)
   * document in the returned page, or `null` when the page is empty. Pass this
   * back to the next `execute` call as `lastTimestamp` to advance the cursor.
   */
  lastTimestamp: number | null;
  /**
   * `true` when the returned page is the same size as the requested `limit`
   * (so another page may exist). Mirrors `community_data_source.dart:74`.
   */
  hasMore: boolean;
}

export class FetchCommunityMessagesUseCase {
  private communityService: CommunityService;

  constructor(communityService: CommunityService = CommunityService.getInstance()) {
    this.communityService = communityService;
  }

  /**
   * Fetch one page of a creator's community-wall messages, newest-first.
   *
   * @param owner - The creator's firebaseId whose wall is read.
   * @param lastTimestamp - The `createdTimestamp` of the last document from the
   *   previous page, or `null` for the first page.
   * @param limit - Page size (typically
   *   `SERVER_FETCH_CONSTANTS.MESSAGES_LIST_PAGINATION = 20`).
   */
  async execute(
    owner: string,
    lastTimestamp: number | null,
    limit: number,
  ): Promise<FetchCommunityMessagesResult> {
    const { messages, lastTimestamp: nextLastTimestamp, hasMore } =
      await this.communityService.fetchMessagesPage(owner, lastTimestamp, limit);

    // Re-assert the desc-by-createdTimestamp sort defensively — mirrors
    // fetchChatMessagesUseCase.ts:122-125 and the Firestore query's
    // `orderBy("createdTimestamp", descending: true)`
    // (community_data_source.dart:62). Lower index = newer message, matching
    // the reversed VirtualList convention used by the community message list.
    const sorted = [...messages].sort(sortCommunityMessagesByCreatedDesc);

    // `hasMore` is already derived by the service with strict `=== limit`
    // (communityService.ts:94, parity with community_data_source.dart:74); the
    // use case forwards it unchanged.
    return {
      messages: sorted,
      lastTimestamp: nextLastTimestamp,
      hasMore,
    };
  }
}

export const fetchCommunityMessagesUseCase = new FetchCommunityMessagesUseCase();
